import { useState } from 'react';
import { useAuth } from '@/lib/auth';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { parseErr } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Snowflake } from 'lucide-react';
import { Logo } from '@/components/brand/Logo';

export default function LoginPage() {
  const { login, meta } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      const to = loc.state?.from || '/home';
      if (u.status === 'pending') nav('/pending');
      else if (u.force_password_change) nav('/change-password', { replace: true });
      else nav(to);
    } catch (e) {
      const msg = parseErr(e);
      if (msg === 'wrong_domain') { nav('/wrong-domain'); return; }
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-10 border-r" style={{ background: 'hsl(var(--surface-1))', borderColor: 'hsl(var(--border))' }}>
        <div className="flex items-center gap-3">
          <Logo variant="horizontal" surface="light" height={44} testId="login-brand-logo" className="max-w-[280px]" />
        </div>
        <div>
          <h1 className="font-heading text-4xl font-semibold leading-tight tracking-tight">The quiet system<br/>that runs the studio.</h1>
          <p className="mt-4 text-[hsl(var(--foreground-muted))] max-w-md text-[15px] leading-relaxed">
            Knowledge, projects, work orders, notifications, and KRA dashboards—one calm surface for the entire team.
          </p>
          <div className="mt-8 flex items-center gap-2 text-[13px] text-[hsl(var(--foreground-subtle))]">
            <Snowflake size={16} />
            <span>Design consultancy for signage & wayfinding</span>
          </div>
        </div>
        <div className="text-[12px] text-[hsl(var(--foreground-subtle))]">
          Sign-in is restricted to <span className="font-mono">@{meta?.allowed_email_domain || '32december.design'}</span>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-10">
        <form onSubmit={submit} className="w-full max-w-sm space-y-4" data-testid="login-form">
          <div className="lg:hidden flex justify-center mb-2">
            <Logo variant="stacked" surface="light" height={90} testId="login-mobile-logo" />
          </div>
          <div className="space-y-1">
            <h2 className="font-heading text-2xl font-semibold">Sign in</h2>
            <p className="text-sm text-[hsl(var(--foreground-muted))]">Use your @{meta?.allowed_email_domain} account.</p>
          </div>
          <label className="block">
            <span className="text-[12px] font-medium text-[hsl(var(--foreground-muted))]">Work email</span>
            <Input data-testid="login-email" required type="email" value={email} onChange={e => setEmail(e.target.value)}
                   placeholder={`you@${meta?.allowed_email_domain}`} className="mt-1" autoComplete="email" />
          </label>
          <label className="block">
            <span className="text-[12px] font-medium text-[hsl(var(--foreground-muted))]">Password</span>
            <Input data-testid="login-password" required type="password" value={password} onChange={e => setPassword(e.target.value)}
                   placeholder="••••••••" className="mt-1" autoComplete="current-password" />
          </label>
          <Button data-testid="login-submit" disabled={busy} type="submit" className="w-full">Sign in</Button>
          <div className="text-[12.5px] text-center">
            <Link className="text-[hsl(var(--foreground-muted))] underline" to="/forgot-password" data-testid="login-forgot-password">
              Forgot password?
            </Link>
          </div>
          <div className="text-[12.5px] text-center text-[hsl(var(--foreground-muted))]">
            New teammate? <Link className="underline" to="/register" data-testid="login-to-register">Register</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
